import _isEqual from 'underscore/cjs/isEqual.js'

/*
 | ==========================================================================
 | Helpers that reduce a vault edit down to the fields the member changed.
 |
 | Every vault funnel seeds its form from the vault's full stored payload and
 | hands the merged object back to `vaultStore.update`, so an unfiltered PATCH
 | re-posts everything the vault holds — including bill-parse data the member
 | never saw on screen. Diffing against the stored payload keeps the request
 | to the edited fields only.
 | ==========================================================================
 */

// Sent on every PATCH so the API can resolve the payload schema for the vault.
const ALWAYS_SENT_KEYS = ['vertical']

// Form controls surface an unset value as '' where the stored payload holds null
// or omits the key, so the two are treated as the same "no value".
const isBlank = value => value === '' || value === null || value === undefined

// Keys that pin an object to a record in a lookup API (lender, policy, provider…).
const IDENTITY_KEYS = ['id', 'uuid', 'product_id', 'product_item_id', 'fund_code', 'code']

const isPlainObject = value => !!value && typeof value === 'object' && !Array.isArray(value)

/*
 * Whether two values point at the same record in a lookup API. Funnels re-hydrate
 * these objects from their source on every pass, so the member keeping the same
 * lender or policy still yields a differently shaped object.
 */
function isSameRecord (value, stored) {
  if (!isPlainObject(value) || !isPlainObject(stored)) return false
  const shared = IDENTITY_KEYS.filter(key => !isBlank(value[key]) && !isBlank(stored[key]))
  return shared.length > 0 && shared.every(key => String(value[key]) === String(stored[key]))
}

/*
 * Locate the stored payload for a vault so it can be used as the diff baseline.
 * Funnels address vaults by uuid or by numeric id, so match on either.
 * @param {Object} store - the vault store instance
 * @param {string|number} productId
 * @returns {Object|null}
 */
export function findStoredVaultPayload (store, productId) {
  if (!productId) return null
  const id = String(productId)
  const matches = vault => [vault?.uuid, vault?.id]
    .some(candidate => candidate !== undefined && candidate !== null && String(candidate) === id)
  const vault = matches(store?.product) ? store.product : store?.products?.find(matches)
  return vault?.payload ?? null
}

/*
 * Resolve the payload to diff the edit against. The server copy wins: the store
 * copy is only refreshed by a full vault list fetch and is persisted across
 * sessions, so after an earlier edit it still holds the pre-edit values and
 * would make those fields look edited again.
 * @param {Object} store - the vault store instance
 * @param {string|number} productId
 * @param {Function} [fetchVaultPayload] - async lookup returning the stored payload
 * @returns {Promise<Object|null>}
 */
export async function resolveVaultBaselinePayload (store, productId, fetchVaultPayload) {
  if (!productId) return null
  const fetched = fetchVaultPayload ? await fetchVaultPayload(productId) : null
  return fetched ?? findStoredVaultPayload(store, productId)
}

/*
 * Keep only the entries that differ from the stored payload.
 * Without a baseline the form data is sent as-is, so a cold store never
 * silently drops a genuine edit.
 * @param {Object} formData
 * @param {Object|null} baseline - the vault's stored payload
 * @returns {Object}
 */
export function buildVaultUpdatePayload (formData = {}, baseline = null) {
  if (!baseline || typeof baseline !== 'object' || Array.isArray(baseline)) {
    return { ...formData }
  }
  return Object.fromEntries(
    Object.entries(formData).filter(([key, value]) => {
      if (ALWAYS_SENT_KEYS.includes(key)) return true
      const stored = baseline[key]
      if (isBlank(value) && isBlank(stored)) return false
      if (isSameRecord(value, stored)) return false
      return !_isEqual(value, stored)
    })
  )
}
